import { Box, Typography } from '@mui/material';
import CustomButton, { CustomButtonType } from 'src/components/common/Button';
import PopUp from 'src/components/common/Popup';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import Input, { Label } from '../form/input';
import VersionSwitch from './versionSwitch';

export interface IPopUp {
  open: boolean;
  onClose: () => void;
  onSubmit: (params: { isV2: boolean }) => void;
  confirmId?: string | number;
  defaultVersion?: 'v1' | 'v2';
  sx?: any;
  children?: any;
  className?: string;
  title?: string;
  actionTitle?: string;
}

const RevertConfirmPopup = ({
  className,
  children,
  title,
  sx,
  open,
  onClose,
  onSubmit,
  actionTitle = 'Revert',
  confirmId,
  defaultVersion = 'v2',
  ...props
}: IPopUp) => {
  const { t, i18n } = useTranslation();

  const [value, setValue] = useState<string>('');
  const [hasError, setError] = useState<boolean>(false);
  const [isV2, setIsV2] = useState<boolean>(defaultVersion === 'v2');

  const canConfirm = useMemo(
    () => String(value) === String(confirmId ?? ''),
    [value, confirmId],
  );

  useEffect(() => {
    if (open) {
      setValue('');
      setError(false);
      setIsV2(defaultVersion === 'v2');
    }
  }, [open, defaultVersion]);

  const onAgree = () => {
    if (String(value) !== String(confirmId ?? '')) {
      setError(true);
      return;
    } else {
      setError(false);
    }
    onSubmit({ isV2 });
    onClose();
  };

  const onChange = (e: any) => {
    setValue(e.target?.value);
    if (String(e.target?.value) === String(confirmId ?? '')) setError(false);
  };

  return (
    <PopUp
      open={open}
      className={`confirm_modal snapshot_revert_modal`}
      title={title}
      onClose={onClose}
    >
      {children}
      <Box mb={1}>
        <Typography
          variant="body2"
          color="#5C677E"
          align="center"
          fontSize={16}
        >
          {t('Reverting will restore the map data to the selected snapshot.')}
        </Typography>
      </Box>
      <Box
        mt={4}
        sx={{
          '& .error': {
            color: '#eb5757',
            textAlign: 'left',
          },
        }}
      >
        <Input
          label={t('Enter "{{id}}" to confirm.', { id: confirmId })}
          value={value}
          onChange={onChange}
        />
        {hasError && (
          <Label className="error">{t('Incorrect snapshot ID')}</Label>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <VersionSwitch
          defaultVersion={defaultVersion}
          onVersionChange={setIsV2}
          value={isV2}
        />
      </Box>
      <Box
        component="div"
        sx={{
          display: 'grid',
          gridTemplateColumns:
            i18n.resolvedLanguage === 'ja' ? '120px 70px' : '70px 70px',
          justifyContent: 'flex-end',
          gridGap: '8px',
          '& button:nth-child(2)': {
            background: '#eb5757',
          },
        }}
      >
        <CustomButton
          buttonType={CustomButtonType.WHITE_BG}
          title={t('Cancel')}
          onClick={onClose}
        />
        <CustomButton
          buttonType={CustomButtonType.NO_ICON}
          title={t('Confirm')}
          onClick={onAgree}
          disabled={!canConfirm}
        />
      </Box>
    </PopUp>
  );
};

export default RevertConfirmPopup;
